import { ArrowLeftIcon, TrashIcon } from "@heroicons/react/24/outline";
import { Link, router } from "@inertiajs/react";
import Layout from "@/components/Layout";
import type { Persona } from "@/types";

type ShowProps = {
  persona: Persona;
};

export default function Show({ persona }: ShowProps) {
  function deletePersona() {
    if (window.confirm(`Are you sure you want to delete ${persona.name}? This action cannot be undone.`)) {
      router.delete(`/personas/${persona.id}`);
    }
  }

  return (
    <Layout title={persona.name || "Persona Details"}>
      <div className="mb-6 flex items-center">
        <Link href="/personas" className="mr-4 text-gray-500 hover:text-primary" aria-label="Back to Personas">
          <ArrowLeftIcon className="h-6 w-6" />
        </Link>
        <h1 className="text-2xl font-semibold">{persona.name}</h1>
      </div>

      <article className="card mx-auto max-w-5xl">
        <div className="card-body space-y-8">
          <section>
            <h2 className="mb-4 border-b border-gray-200 pb-2 text-lg font-semibold text-gray-800">
              Basic Information
            </h2>
            <div className="grid gap-6 md:grid-cols-2">
              <Detail label="Persona Name" value={persona.name} />
              <Detail label="Technical Proficiency" value={persona.tech_level} />
            </div>
            <div className="mt-6">
              <Detail label="Description" value={persona.description} />
            </div>
          </section>

          <section>
            <h2 className="mb-4 border-b border-gray-200 pb-2 text-lg font-semibold text-gray-800">Demographics</h2>
            <div className="grid gap-6 md:grid-cols-2">
              <Detail label="Age Range" value={persona.age_range} />
              <Detail label="Country" value={persona.country} />
              <Detail label="Primary Language" value={persona.language} />
              <Detail label="Occupation" value={persona.occupation} />
            </div>
          </section>

          <section>
            <h2 className="mb-4 border-b border-gray-200 pb-2 text-lg font-semibold text-gray-800">
              Authentication Settings
            </h2>
            <Detail label="Authentication Method" value={authMethodLabel(persona.auth_method)} />

            {persona.auth_method === "credentials" && (
              <div className="mt-4 grid gap-6 md:grid-cols-2">
                <Detail label="Username" value={persona.auth_username} />
                <Detail label="Password" value="••••••••" />
              </div>
            )}

            {persona.auth_method === "instructions" && (
              <div className="mt-4">
                <Detail label="Authentication Instructions" value={persona.auth_instructions} />
              </div>
            )}
          </section>

          <div className="flex flex-col justify-between gap-3 lg:flex-row lg:items-center">
            <Link href="/personas" className="mb-4 text-primary hover:text-primary/90 hover:underline lg:mb-0">
              Back to Personas
            </Link>
            <div className="flex flex-col-reverse gap-3 lg:flex-row">
              <button className="btn btn-error-gray" onClick={deletePersona} type="button">
                <TrashIcon className="h-5 w-5 text-gray-500" />
                Delete Persona
              </button>
              <Link href={`/personas/${persona.id}/edit`} className="btn btn-primary">
                Edit Persona
              </Link>
            </div>
          </div>
        </div>
      </article>
    </Layout>
  );
}

function Detail({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <p className="mb-1 text-sm text-gray-500">{label}</p>
      <p className="font-medium">{value || "Not specified"}</p>
    </div>
  );
}

function authMethodLabel(method: Persona["auth_method"]) {
  if (method === "credentials") {
    return "Username & Password";
  }

  if (method === "instructions") {
    return "Custom Authentication Instructions";
  }

  return "No authentication needed";
}
